// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAccessibleSubmit from "@insite/client-framework/Common/Hooks/useAccessibleSubmit";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import QtyField, { QtyFieldStyle } from "@steiner/App/Components/Qty/QtyField";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    /**
     * If true, the qtyOrdered will display in a text field for editing.
     * If false, the qtyOrdered will display as read-only text.
     * Default value: false
     */
    editable?: boolean;
    onQtyOrderedChange?: (qtyOrdered: number) => void;
    extendedStyles?: CartLineQuantityStyles;
    label?: string;
    // XNG-Changes
    showLabel?: boolean;
    showAsNumbers?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    isLoading: state.data.carts.isLoading,
    cartLoadedTime: state.data.carts.cartLoadedTime,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasCartLineContext;

export interface CartLineQuantityStyles {
    editableQuantityTextField?: QtyFieldStyle;
    readOnlyQuantityText?: SmallHeadingAndTextStyles;
    // xng-change
    mobileQtyLabel?: TypographyProps;
    mobileQtyHiddenWrapper?: HiddenProps;
}

export const cartLineQuantityStyles: CartLineQuantityStyles = {
    editableQuantityTextField: {
        labelProps: {
            variant: "legend",
            css: css`
                font-size: 10px;
                text-transform: uppercase;
                font-weight: 600;
            `,
        },
    },
};

const CartLineQuantity: FC<Props> = ({
    cart,
    cartLine,
    editable = false,
    label,
    isLoading,
    cartLoadedTime,
    onQtyOrderedChange = () => {},
    extendedStyles,
    // xng-change
    showLabel,
    showAsNumbers = false,
}) => {
    const localOnQtyOrderedChange = (value: string, onError?: () => void) => {
        const inputQty = parseFloat(value);
        if (Number.isNaN(inputQty)) {
            onError?.();
            return;
        }

        onQtyOrderedChange(inputQty);
    };

    const { value, changeHandler, keyDownHandler, blurHandler } = useAccessibleSubmit(
        cartLine.qtyOrdered!.toString(),
        localOnQtyOrderedChange,
        cartLoadedTime,
    );

    const [styles] = React.useState(() => mergeToNew(cartLineQuantityStyles, extendedStyles));

    // xng-change
    const xBlurHandler = (event: any) => {
        const value = event?.currentTarget?.value;
        if (!value) {
            event.currentTarget = { value: cartLine.qtyOrdered };
            changeHandler(event);
            return;
        }
        blurHandler();
    };

    if (editable && !isOutOfStock(cartLine)) {
        return (
            <QtyField
                label={label || translate("Qty")}
                value={value}
                disabled={!cart.canModifyOrder || cartLine.isPromotionItem || cart.type === "Job" || isLoading[cart.id]}
                onChange={changeHandler}
                onKeyDown={keyDownHandler}
                onBlur={xBlurHandler}
                onClick={event => event.currentTarget.focus()}
                data-test-selector="cartline_qty"
                // xng-change
                showLabel={showLabel}
                extendedStyles={styles.editableQuantityTextField}
            />
        );
    }

    if (!editable && showAsNumbers) {
        return (
            <StyledWrapper>
                <Hidden above="sm" {...styles.mobileQtyHiddenWrapper}>
                    <Typography {...styles.mobileQtyLabel}>{label}</Typography>
                </Hidden>
                <Typography>{cartLine.qtyOrdered}</Typography>
            </StyledWrapper>
        );
    }

    return (
        <QtyField
            label={label || translate("Qty")}
            value={cartLine.qtyOrdered!}
            disabled={true}
            editable={false}
            data-test-selector="cartline_qty"
            // xng-change
            showLabel={showLabel}
            extendedStyles={styles.editableQuantityTextField}
        />
    );
};

export default connect(mapStateToProps)(withCartLine(CartLineQuantity));
